import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { io as ioClient } from 'socket.io-client';

const SocketContext = createContext({ socket: null, connected: false });

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (window.location.protocol + '//' + window.location.hostname + ':4000');

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const listenersRef = useRef(new Map());

  useEffect(() => {
    if (!user) return;

    const token = api.getToken();
    if (!token) return;

    const sock = ioClient(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      timeout: 10000,
      auth: { token },
    });

    sock.on('connect', () => {
      setConnected(true);
    });

    sock.on('disconnect', () => setConnected(false));
    sock.on('connect_error', () => setConnected(false));

    socketRef.current = sock;

    return () => {
      sock.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [user]);

  const emit = useCallback((event, data) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    }
  }, []);

  const on = useCallback((event, handler) => {
    if (socketRef.current) {
      socketRef.current.on(event, handler);
      listenersRef.current.set(event + handler.toString(), { event, handler });
    }
  }, []);

  const off = useCallback((event, handler) => {
    if (socketRef.current) {
      socketRef.current.off(event, handler);
      listenersRef.current.delete(event + handler.toString());
    }
  }, []);

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, connected, emit, on, off }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
